import { parseExtensionId } from '@atlassian/forge-ui/utils/parseExtensionId';
import type {
	Command,
	FullPage,
} from '@atlassian/jira-forge-ui-types/src/common/types/extension.tsx';
import type { RouterActionsType } from '@atlassian/react-resource-router';

import { getItemHref as getAdminPageHref } from '@atlassian/jira-navigation-apps-sidebar-forge/src/ui/admin-section/index.tsx';
import { getItemHref as getProjectPageHref } from '@atlassian/jira-navigation-apps-sidebar-forge/src/ui/project-section/index.tsx';
import { getItemHref as getPersonalPageHref } from '@atlassian/jira-navigation-apps-sidebar-forge/src/ui/personal-settings-section/index.tsx';
import { getItemHref as getServiceDeskQueuePageHref } from '@atlassian/jira-navigation-apps-sidebar-forge/src/ui/servicedesk-queue/index.tsx';
import { getItemHref as getProjectSettingsPageHref } from '@atlassian/jira-navigation-apps-sidebar-forge/src/ui/project-settings-section/index.tsx';

import { getItemHref as getGlobalPageHref } from '@atlassian/jira-atlassian-navigation/src/controllers/addons/forge/index.tsx';

import { getItemHref as getAdminPageHrefV4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-forge/src/ui/admin-section/index.tsx';
import { getItemHref as getProjectPageHrefV4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-forge/src/ui/project-section/index.tsx';
import { getItemHref as getPersonalPageHrefV4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-forge/src/ui/personal-settings-section/index.tsx';
import { getItemHref as getServiceDeskQueuePageHrefV4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-forge/src/ui/servicedesk-queue/index.tsx';
import { getItemHref as getProjectSettingsPageHrefV4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-forge/src/ui/project-setting-section/index.tsx';

import type { ProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import { functionWithFF } from '@atlassian/jira-feature-flagging-utils';

export const findMatchingModule = ({
	pageModules,
	target,
	rawCommandPaletteExtensionId,
}: {
	pageModules: Array<FullPage>;
	target: Command['properties']['target'];
	rawCommandPaletteExtensionId: string;
}) =>
	pageModules.find((page) => {
		if (!('page' in target)) return false;

		const commandPaletteExtensionId = parseExtensionId(rawCommandPaletteExtensionId);

		const { appId, environmentId, moduleKey } = parseExtensionId(page.id);

		const isExtensionMatch =
			appId === commandPaletteExtensionId.appId &&
			environmentId === commandPaletteExtensionId.environmentId &&
			moduleKey === target.page;

		return isExtensionMatch;
	});

const navigateToPageOldNav = ({
	pageModule,
	push,
	projectContext,
}: {
	pageModule: FullPage;
	push: RouterActionsType['push'];
	projectContext: ProjectContext | null;
}) => {
	switch (pageModule.type) {
		case 'jira:adminPage':
			push(getAdminPageHref(pageModule));
			break;
		case 'jira:projectPage':
			if (!projectContext) break;
			push(getProjectPageHref(pageModule, projectContext.projectType, projectContext.projectKey));
			break;
		case 'jira:globalPage':
			push(getGlobalPageHref(pageModule));
			break;
		case 'jira:projectSettingsPage':
			if (!projectContext) break;
			push(
				getProjectSettingsPageHref(
					pageModule,
					projectContext.projectType,
					projectContext.projectKey,
				),
			);
			break;
		case 'jira:personalSettingsPage':
			push(getPersonalPageHref(pageModule));
			break;
		case 'jiraServiceManagement:queuePage':
			if (!projectContext) break;
			push(getServiceDeskQueuePageHref(pageModule, projectContext.projectKey));
			break;
		default:
			// This has no impact on runtime but whenever new page module will be added this will fail and will require to handle that case
			pageModule satisfies never;
			break;
	}
};

export const navigateToPageV4 = ({
	pageModule,
	push,
	projectContext,
}: {
	pageModule: FullPage;
	push: RouterActionsType['push'];
	projectContext: ProjectContext | null;
}) => {
	switch (pageModule.type) {
		case 'jira:adminPage':
			push(getAdminPageHrefV4(pageModule));
			break;
		case 'jira:projectPage':
			if (!projectContext) break;
			push(getProjectPageHrefV4(pageModule, projectContext.projectType, projectContext.projectKey));
			break;
		case 'jira:globalPage':
			push(getGlobalPageHref(pageModule));
			break;
		case 'jira:projectSettingsPage':
			if (!projectContext) break;
			push(
				getProjectSettingsPageHrefV4(
					pageModule,
					projectContext.projectType,
					projectContext.projectKey,
				),
			);
			break;
		case 'jira:personalSettingsPage':
			push(getPersonalPageHrefV4(pageModule));
			break;
		case 'jiraServiceManagement:queuePage':
			if (!projectContext) break;
			push(getServiceDeskQueuePageHrefV4(pageModule, projectContext.projectKey));
			break;
		default:
			// This has no impact on runtime but whenever new page module will be added this will fail and will require to handle that case
			pageModule satisfies never;
			break;
	}
};

export const navigateToPage = functionWithFF(
	// TODO: Find this ff out
	'forge-command-palette',
	navigateToPageV4,
	navigateToPageOldNav,
);
